<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'

const props = withDefaults(defineProps<{
    direction?: 'left' | 'right' | 'bottom',
    widthClass?: string,
    unwrap?: boolean,
}>(), {
    direction: 'left',
    widthClass: 'w-[80%]',
    unwrap: false,
})

const attrs = useAttrs()
const open = defineModel<boolean>()

const sidenavOverlayEl = shallowRef()
const { activate, deactivate } = useFocusTrap(sidenavOverlayEl, {
    allowOutsideClick: true,
    fallbackFocus: () => sidenavOverlayEl.value,
})
onClickOutside(sidenavOverlayEl, () => open.value = false)

/**
 * shouldRender is exposed as a slot prop for consumers to conditionally mount
 * their content. It stays true for the duration of the close animation (300ms)
 * so that content-driven panels (e.g. direction="bottom") retain their height
 * while sliding out, preventing the panel from collapsing to 0px and making
 * the transition invisible. After the animation completes, it becomes false
 * and the content unmounts — keeping each open cycle fresh.
 */
const isAnimatingClose = ref(false)
let closeTimer: ReturnType<typeof setTimeout> | null = null
const shouldRender = computed(() => open.value || isAnimatingClose.value)

watch(open, async (newVal) => {
    if (newVal) {
        if (closeTimer) clearTimeout(closeTimer)
        isAnimatingClose.value = false
        await nextTick()
        void activate()
    } else {
        void deactivate()
        isAnimatingClose.value = true
        closeTimer = setTimeout(() => { isAnimatingClose.value = false }, 300)
    }
})
</script>

<template>
    <ClientOnly>
        <slot v-if="unwrap" />
        <Teleport v-else to="#teleports">
            <div>
                <Transition
                    enter-active-class="transition-opacity duration-300 ease-in"
                    enter-from-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="transition-opacity duration-300 ease-out"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <div
                        v-if="open"
                        class="fixed h-screen w-screen top-0 left-0 bg-black/40 cursor-pointer z-999"
                    />
                </Transition>

                <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
                <div
                    ref="sidenavOverlayEl"
                    role="dialog"
                    tabindex="-1"
                    class="fixed z-999 overflow-x-hidden transition-transform duration-300 no-scrollbar bg-base-100"
                    :class="[
                        {
                            'h-full top-0 left-0 right-auto origin-left': props.direction === 'left',
                            'h-full top-0 right-0 left-auto origin-right': props.direction === 'right',
                            'bottom-0 top-auto left-0 right-0 max-h-[90vh]': props.direction === 'bottom',
                            '-translate-x-full': !open && props.direction === 'left',
                            'translate-x-full': !open && props.direction === 'right',
                            'translate-y-full': !open && props.direction === 'bottom',
                            'translate-x-0': open && props.direction !== 'bottom',
                            'translate-y-0': open && props.direction === 'bottom',
                        },
                        props.direction !== 'bottom' ? widthClass : '',
                    ]"
                    v-bind="attrs"
                    @keydown.esc="open = false"
                >
                    <slot :should-render="shouldRender" />
                </div>
            </div>
        </Teleport>

        <!-- Prevent layout shift on desktop view -->
        <template #fallback>
            <slot name="fallback">
                <div class="hidden" />
            </slot>
        </template>
    </ClientOnly>
</template>
